import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Share2, Copy, Check, ExternalLink, Users, Eye } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { statsApi } from '../services/api';

const ShareSection = () => {
  const [copied, setCopied] = useState(false);
  const [shareCount, setShareCount] = useState(0);
  const [visitorCount, setVisitorCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    // Fetch current counters on mount
    statsApi.getCounters()
      .then((data) => {
        setShareCount(data.shares || 0);
        setVisitorCount(data.visitors || 0);
      })
      .catch(() => {});
  }, []);

  const incrementShare = async (platform) => {
    try {
      const data = await statsApi.registerShare(platform);
      setShareCount(data.shares || 0);
    } catch (err) {
      // Silently ignore counter errors so sharing still works
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = 'Mari bersama menyumbang wakaf untuk membina Kompleks Darul Ummah (KDU) - Sekolah Rendah Islam Integrasi di Sijangkang, Selangor. Setiap sumbangan adalah amal jariah berkekalan!';
  const linktreeUrl = 'https://linktr.ee/sriiduat';

  const handleWhatsAppShare = () => {
    incrementShare('whatsapp');
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    window.open(url, '_blank');
  };

  const handleFacebookShare = () => {
    incrementShare('facebook');
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleTelegramShare = () => {
    incrementShare('telegram');
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  const handleCopyLink = async () => {
    incrementShare('copy');
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for older browsers or restricted contexts
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Pautan Disalin!',
        description: 'Kongsikan pautan ini kepada rakan-rakan anda',
      });
    } catch (err) {
      toast({
        title: 'Pautan',
        description: shareUrl,
      });
    }
  };

  const shareButtons = [
    {
      label: 'WhatsApp',
      onClick: handleWhatsAppShare,
      bgClass: 'bg-[#25D366] hover:bg-[#1da851]',
      testId: 'share-whatsapp-button',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      ),
    },
    {
      label: 'Facebook',
      onClick: handleFacebookShare,
      bgClass: 'bg-[#1877F2] hover:bg-[#0d65d9]',
      testId: 'share-facebook-button',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
    {
      label: 'Telegram',
      onClick: handleTelegramShare,
      bgClass: 'bg-[#0088cc] hover:bg-[#0077b3]',
      testId: 'share-telegram-button',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700 relative overflow-hidden" data-testid="share-section">
      {/* Decorative blur */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-400 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300 rounded-full filter blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-white/15 backdrop-blur-md rounded-2xl mb-8 border border-white/20"
          >
            <Share2 className="w-10 h-10 text-white" />
          </motion.div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Kongsikan Kempen Ini
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Sebarkan kebaikan! Kongsikan kempen wakaf ini kepada keluarga dan rakan-rakan. Setiap perkongsian membawa kita lebih dekat ke matlamat.
          </p>

          {/* Counters */}
          <div className="flex flex-wrap justify-center gap-4 mb-12" data-testid="share-counters">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 bg-white/15 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/20"
            >
              <Users className="w-6 h-6 text-purple-100" />
              <div className="text-left">
                <div className="text-2xl font-bold text-white" data-testid="share-count">
                  {shareCount.toLocaleString()}
                </div>
                <div className="text-xs text-purple-200">orang telah berkongsi</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center gap-3 bg-white/15 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/20"
            >
              <Eye className="w-6 h-6 text-purple-100" />
              <div className="text-left">
                <div className="text-2xl font-bold text-white" data-testid="visitor-count">
                  {visitorCount.toLocaleString()}
                </div>
                <div className="text-xs text-purple-200">jumlah pelawat</div>
              </div>
            </motion.div>
          </div>

          {/* Share buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {shareButtons.map((btn, index) => (
              <motion.button
                key={btn.label}
                data-testid={btn.testId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.95 }}
                onClick={btn.onClick}
                className={`flex items-center gap-3 px-8 py-4 rounded-2xl text-white font-semibold shadow-lg hover:shadow-xl transition-all ${btn.bgClass}`}
              >
                {btn.icon}
                <span>{btn.label}</span>
              </motion.button>
            ))}

            {/* Copy link button */}
            <motion.button
              data-testid="share-copy-link-button"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCopyLink}
              className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-white/15 backdrop-blur-md hover:bg-white/25 text-white font-semibold shadow-lg hover:shadow-xl transition-all border border-white/20"
            >
              {copied ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />}
              <span>{copied ? 'Disalin!' : 'Salin Pautan'}</span>
            </motion.button>
          </div>

          {/* Linktree link */}
          <motion.a
            href={linktreeUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="linktree-button"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            className="inline-flex items-center gap-2 text-purple-100 hover:text-white transition-colors text-lg font-medium underline underline-offset-4 decoration-purple-300/50 hover:decoration-white"
          >
            Ikuti semua media sosial kami
            <ExternalLink className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ShareSection;
